<template>
  <div>
    <vetster-input
      v-if="isInputVisible('name')"
      id="name"
      ref="nameInput"
      v-model="inputs.name"
      type="text"
      name="name"
      is-rebranded
      class="mb-6"
      :label="$t('pets.pets-name')"
      :shake="shouldShake('name', v$.inputs.name.$error)"
      :error="hasError('name', v$.inputs.name.$error)"
      :feedback="getErrorMessage('name', v$.inputs.name.$error, $t('pets.errors.please-enter-your-pet-s-name'))"
    />

    <div class="divide-y space-y-4">
      <vetster-nav-item
        v-if="isInputVisible('species')"
        :right-icon="['fal', 'angle-right']"
        @click="openSpeciesForm"
      >
        <template #heading>
          <span class="text-md text-neutral-900">
            {{ $t('pets.species-label') }}
          </span>
        </template>
        <template #sub-heading>
          <span class="text-sm text-neutral-500">
            {{ speciesLabel }}
          </span>
        </template>
      </vetster-nav-item>

      <vetster-nav-item
        v-if="isInputVisible('breed')"
        :badge="badgeLabel('breed')"
        :badge-classes="badgeClasses"
        badge-variant="red"
        :right-icon="['fal', 'angle-right']"
        class="pt-4"
        @click="openPetForm(PetDetailsForm.Breed)"
      >
        <template #heading>
          <span class="text-md text-neutral-900">
            {{ $t('pets.breed') }}
          </span>
        </template>
        <template #sub-heading>
          <span class="text-sm text-neutral-500">
            {{ breedLabel }}
          </span>
        </template>
      </vetster-nav-item>

      <vetster-nav-item
        v-if="isInputVisible('gender')"
        :badge="badgeLabel('gender')"
        :badge-classes="badgeClasses"
        badge-variant="red"
        :right-icon="['fal', 'angle-right']"
        class="pt-4"
        @click="openPetForm(PetDetailsForm.Sex)"
      >
        <template #heading>
          <span class="text-md text-neutral-900">
            {{ $t('pets.sex') }}
          </span>
        </template>
        <template #sub-heading>
          <span class="text-sm text-neutral-500">
            {{ sexLabel }}
          </span>
        </template>
      </vetster-nav-item>

      <vetster-nav-item
        v-if="isInputVisible('birthday_on')"
        :badge="badgeLabel('birthday_on')"
        :badge-classes="badgeClasses"
        badge-variant="red"
        :right-icon="['fal', 'angle-right']"
        class="pt-4"
        @click="openPetForm(PetDetailsForm.BirthDate)"
      >
        <template #heading>
          <span class="text-md text-neutral-900">
            {{ $t('search.birth-date') }}
          </span>
        </template>
        <template #sub-heading>
          <span
            v-if="inputs.birthday_on"
            class="text-sm text-neutral-500"
          >
            {{ birthday }} (<vetster-pet-age :pet="inputs" />)
          </span>
          <span
            v-else
            class="text-sm text-neutral-500"
          >
            {{ $t('search.tap-to-add') }}
          </span>
        </template>
      </vetster-nav-item>

      <vetster-nav-item
        v-if="isInputVisible('weight')"
        :badge="badgeLabel('weight')"
        :badge-classes="badgeClasses"
        badge-variant="red"
        :right-icon="['fal', 'angle-right']"
        class="pt-4"
        @click="openPetForm(PetDetailsForm.Weight)"
      >
        <template #heading>
          <span class="text-md text-neutral-900">
            {{ $t('pets.weight') }}
          </span>
        </template>
        <template #sub-heading>
          <span class="text-sm text-neutral-500">
            {{ weightLabel }}
          </span>
        </template>
      </vetster-nav-item>
    </div>

    <vetster-pet-form-slide-over
      ref="petFormSlideOver"
      :form="petFormType"
      :inputs="inputs"
      :is-top-slide-over="isInSlideOver"
      :container-classes="containerClasses"
      @close="focusPetName"
      @updateInputs="updateInputs"
    />
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import keys from 'lodash/keys';
import pick from 'lodash/pick';
import { DateTime } from 'luxon';

import VetsterNavItem from '@/brand-components/VetsterNavItem';
import VetsterPetAge from '@/brand-components/VetsterPetAge';
import VetsterPetFormSlideOver from '@/brand-components/VetsterPetFormSlideOver';
import Gender from '@/enums/Gender';
import PetDetailsForm from '@/enums/PetDetailsForm';
import PetSpecies from '@/enums/PetSpecies';
import { titleCase } from '@/helpers/format';
import useWeightHelpers from '@/helpers/use-weight-helpers';
import ParseErrors from '@/mixins/ParseErrors';

export default {
  name: 'VetsterPetForm',

  components: {
    VetsterNavItem,
    VetsterPetAge,
    VetsterPetFormSlideOver,
  },

  mixins: [ParseErrors],

  props: {
    hidePrefilledInputs: {
      type: Boolean,
      default: false,
    },
    isInSlideOver: {
      type: Boolean,
      default: true,
    },
    pet: {
      type: Object,
      default: null,
    },
    containerClasses: {
      type: String,
      default: null,
    },
    requiredInputs: {
      type: Array,
      default: () => [],
    },
    showWeight: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const { getWeight } = useWeightHelpers();

    return {
      getWeight,
      v$: useVuelidate(),
    };
  },

  data() {
    return {
      badgeClasses: '',
      inputs: {
        name: '',
        birthday_on: '',
        breed: '',
        gender: '',
        spayed_neutered: '',
        species: PetSpecies.Dog,
        weight: null,
      },
      PetDetailsForm,
      petFormType: '',
      show: {
        name: true,
        birthday_on: true,
        breed: true,
        gender: true,
        species: true,
        weight: this.showWeight,
      },
    };
  },

  validations() {
    return {
      inputs: {
        name: {
          required: requiredIf(() => this.isInputRequired('name')),
        },
        birthday_on: {
          required: requiredIf(() => this.isInputRequired('birthday_on')),
        },
        breed: {
          required: requiredIf(() => this.isInputRequired('breed')),
        },
        gender: {
          required: requiredIf(() => this.isInputRequired('gender')),
        },
        weight: {
          required: requiredIf(() => this.isInputRequired('weight')),
        },
      },
    };
  },

  computed: {
    birthday() {
      return DateTime.fromISO(this.inputs.birthday_on).toLocaleString(DateTime.DATE_MED);
    },

    breedLabel() {
      return this.inputs.breed || this.$t('search.tap-to-add');
    },

    sexLabel() {
      const { gender } = this.inputs;

      if (gender) {
        return `${this.$t(`common.genders.${gender}`)} / ${this.spayedNeuteredLabel}`;
      }

      return this.$t('search.tap-to-add');
    },

    spayedNeuteredLabel() {
      const { gender } = this.inputs;

      if (this.inputs.spayed_neutered === false) {
        return gender === Gender.Female
          ? this.$t('search.not-spayed')
          : this.$t('search.not-neutered');
      }

      switch (gender) {
        case Gender.Female:
          return this.$t('search.spayed');
        case Gender.Male:
          return this.$t('search.neutered');
        default:
          return this.$t('search.spayed');
      }
    },

    speciesLabel() {
      return this.speciesName || this.$t('search.tap-to-add');
    },

    speciesName() {
      const { species } = this.inputs;

      return this.$tOr(
        `pets.species.${species}`,
        species ? titleCase(species) : null,
      );
    },

    weightLabel() {
      return this.inputs.weight
        ? this.getWeight(this.inputs.weight)
        : this.$t('search.tap-to-add');
    },
  },

  created() {
    this.setInitialInputs();
    this.setInputsVisibility();
    this.focusPetName();
  },

  methods: {
    badgeLabel(input) {
      return this.showRequiredBadge(input) ? this.$t('app.required') : null;
    },

    checkInputError(input) {
      return this.v$.inputs[input].$dirty && this.v$.inputs[input].$error;
    },

    focusPetName() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.$refs.nameInput?.focus();
        }, 400);
      });
    },

    getPayload() {
      const visibleInputs = filter(keys(this.inputs, this.isInputVisible));

      return pick(this.inputs, visibleInputs);
    },

    isInputRequired(input) {
      return this.requiredInputs.includes(input) && this.isInputVisible(input);
    },

    isInputVisible(input) {
      return this.show[input];
    },

    openPetForm(form) {
      this.petFormType = form;
      this.$refs.petFormSlideOver.open();
    },

    openSpeciesForm() {
      this.openPetForm(PetDetailsForm.Species);
    },

    setInitialInputs() {
      if (this.pet) {
        this.inputs = pick(this.pet, keys(this.inputs));
      }
    },

    setInputsVisibility() {
      if (this.hidePrefilledInputs) {
        forEach(keys(this.inputs), (key) => {
          this.show[key] = !this.pet?.[key];
        });
      }
    },

    setSpecies(species) {
      this.inputs.species = species;
    },

    shakeRequiredBadges() {
      this.badgeClasses = 'animated shake fast';

      setTimeout(() => {
        this.badgeClasses = '';
      }, 800);
    },

    showRequiredBadge(input) {
      return !this.inputs[input] && !!this.v$.inputs[input].required?.$invalid;
    },

    updateInputs(updatedInputs) {
      this.$emit('updatedInputs', {
        formType: this.petFormType,
        updatedInputs,
      });
      this.inputs = updatedInputs;
    },

    validate() {
      if (this.hasValidInputs(this.v$.inputs)) {
        return true;
      }

      if (this.checkInputError('name')) {
        this.focusPetName();
      } else {
        this.shakeRequiredBadges();
        this.$toast(this.$t('errors.please-fill-in-all-the-required-information'), { status: 'error' });
      }

      return false;
    },
  },
};
</script>
